<template>
  <div id="pageSetupServer">
    <v-container grid-list-xl fluid>
      <v-layout row wrap>

        <!-- Page header -->
        <v-flex xs12>
          <div class="modern-page-header">
            <div class="modern-page-header__left">
              <div class="modern-page-header__icon-wrap">
                <v-icon class="modern-page-header__icon">cloud_upload</v-icon>
              </div>
              <div>
                <h3 class="modern-page-header__title">Setup Server</h3>
                <p class="modern-page-header__sub">Provision and launch school server instances</p>
              </div>
            </div>
          </div>
        </v-flex>

        <!-- Info banner -->
        <v-flex xs12>
          <div class="setup-info-banner">
            <v-icon class="setup-info-banner__icon">info</v-icon>
            <span>Click <strong>Start</strong> next to a school to provision its server. Running servers are shown with a <strong>Running</strong> badge.</span>
          </div>
        </v-flex>

        <!-- Servers table card -->
        <v-flex lg12>
          <v-card class="modern-table-card">
            <div class="modern-table-toolbar">
              <div class="modern-search-wrap">
                <v-icon class="modern-search-icon">search</v-icon>
                <input
                  v-model="search"
                  class="modern-search-input"
                  placeholder="Search schools..."
                />
              </div>
              <v-btn flat color="secondary" class="table-toolbar-btn" @click="listCompanies">
                <v-icon left small>refresh</v-icon>Refresh
              </v-btn>
            </div>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="complex.headers"
                :search="search"
                :items="complex.items"
                :rows-per-page-items="[{ text: 'All', value: -1 }]"
                item-key="company_id"
                class="modern-data-table"
              >
                <template slot="items" slot-scope="props">
                  <td>
                    <div class="school-name-cell">
                      <div class="school-avatar">
                        {{ props.item.company_name ? props.item.company_name[0].toUpperCase() : 'S' }}
                      </div>
                      <div>
                        <div class="table-cell-primary">{{ props.item.company_name }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell-mono">{{ props.item.company_no }}</td>
                  <td>
                    <div class="port-badge">:{{ props.item.port }}</div>
                  </td>
                  <td class="table-cell-secondary domain-cell">{{ props.item.domain }}</td>
                  <td align="right">
                    <div
                      v-if="props.item.started"
                      class="running-badge"
                    >
                      <span class="running-badge__dot"></span>
                      Running
                    </div>
                    <v-btn
                      v-else
                      small
                      depressed
                      color="primary"
                      class="start-btn"
                      @click="propmtToCreateServer(props.item)"
                    >
                      <v-icon left x-small>play_arrow</v-icon>Start
                    </v-btn>
                  </td>
                </template>
                <template slot="no-data">
                  <div class="modern-empty-state">
                    <v-icon class="modern-empty-state__icon">cloud_upload</v-icon>
                    <p class="modern-empty-state__text">No school servers found</p>
                    <v-btn depressed color="primary" small @click="listCompanies">Refresh</v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- SetupLog dialog (renders when a school is selected) -->
        <SetupLog
          v-if="selectedCompany && selectedCompany.company_no"
          ref="setupLog"
          :company="selectedCompany"
          @started="listCompanies"
        ></SetupLog>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import APIServer from "@/mixins/APIServer";
import APICompany from "@/mixins/APICompany";
import SetupLog from "./SetupLog.vue";
export default {
  mixins: [APIServer, APICompany],
  components: { SetupLog },
  data() {
    return {
      search: "",
      companies: [],
      servers: [],
      selectedCompany: {},
      complex: {
        selected: [],
        headers: [
          { text: "School", value: "company_name" },
          { text: "Identifier", value: "company_no" },
          { text: "Port", value: "port" },
          { text: "Domain", value: "domain" },
          { text: "", value: "", sortable: false }
        ],
        items: []
      }
    };
  },
  created() {
    this.listCompanies();
  },
  methods: {
    listCompanies: async function() {
      try {
        let response = await this.listServers();
        let serverIds = "";
        if (response.data && Array.isArray(response.data)) {
          this.servers = response.data;
          serverIds = response.data.map(obj => obj.name).join(", ");
        }
        this.companies = await this.getCompanies();
        this.complex.items = this.companies.map(obj => {
          let comp = Object.assign({}, obj);
          comp.started = serverIds.includes(obj.company_no);
          return comp;
        });
      } catch (error) {
        this.$toast.error("Failed to load servers.", "Error", { position: "topCenter" });
      }
    },
    propmtToCreateServer(info) {
      this.selectedCompany = info;
      let self = this;
      this.$swal({
        title: "Start server for " + info.company_name + "?",
        text: "This will provision a new server instance.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Start it!",
        cancelButtonText: "Cancel",
        showCloseButton: true,
        showLoaderOnConfirm: true,
        preConfirm: inputValue =>
          new Promise(resolve => resolve(inputValue))
      }).then(async result => {
        if (result.value) {
          await self.setupNewServer();
        }
      });
    },
    setupNewServer: async function() {
      this.$nextTick(() => {
        if (this.$refs.setupLog) {
          this.$refs.setupLog.showDialog();
        }
      });
    }
  }
};
</script>

<style scoped lang="css">
.modern-page-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 0 20px; flex-wrap: wrap; gap: 16px;
}
.modern-page-header__left { display: flex; align-items: center; gap: 16px; }
.modern-page-header__icon-wrap {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #37474F, #263238);
  display: flex; align-items: center; justify-content: center;
}
.modern-page-header__icon { color: #64B5F6 !important; font-size: 22px !important; }
.modern-page-header__title {
  font-family: 'Inter', 'Roboto', sans-serif; font-size: 1.3rem;
  font-weight: 700; color: #1e293b; margin: 0; letter-spacing: -0.3px;
}
.modern-page-header__sub {
  font-family: 'Inter', 'Roboto', sans-serif; font-size: 13px; color: #64748b; margin: 2px 0 0;
}

.setup-info-banner {
  display: flex; align-items: center; gap: 10px;
  background: rgba(2,136,209,0.06); border: 1px solid rgba(2,136,209,0.2);
  border-radius: 10px; padding: 12px 16px;
  font-family: 'Inter', 'Roboto', sans-serif; font-size: 13px; color: #0277BD;
  margin-bottom: 4px;
}
.setup-info-banner__icon { color: #0288D1 !important; font-size: 18px !important; }

.modern-table-card { border-radius: 14px !important; overflow: hidden; }
.modern-table-toolbar { display: flex; align-items: center; padding: 14px 16px; gap: 8px; }
.modern-search-wrap {
  flex: 1; display: flex; align-items: center;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 8px 14px; gap: 10px; max-width: 360px; transition: border-color 0.2s;
}
.modern-search-wrap:focus-within { border-color: #1565C0; background: #ffffff; }
.modern-search-icon { color: #94a3b8 !important; font-size: 18px !important; }
.modern-search-input {
  border: none; outline: none; background: transparent;
  font-family: 'Inter', 'Roboto', sans-serif; font-size: 14px; color: #334155; width: 100%;
}
.modern-search-input::placeholder { color: #94a3b8; }
.table-toolbar-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important; font-weight: 500 !important;
  text-transform: none !important; border-radius: 10px !important; height: 38px !important;
}

.school-name-cell { display: flex; align-items: center; gap: 12px; }
.school-avatar {
  width: 34px; height: 34px; border-radius: 10px;
  background: linear-gradient(135deg, #37474F, #263238);
  color: #64B5F6; font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.table-cell-primary { font-weight: 600 !important; color: #1e293b !important; }
.table-cell-mono {
  font-family: 'SF Mono', 'Roboto Mono', monospace; font-size: 13px !important; color: #475569 !important;
}
.table-cell-secondary { color: #64748b !important; font-size: 14px !important; }
.domain-cell { font-size: 12px !important; }

.port-badge {
  display: inline-flex; align-items: center;
  background: #f1f5f9; color: #475569;
  padding: 3px 10px; border-radius: 6px;
  font-family: 'SF Mono', 'Roboto Mono', monospace; font-size: 13px; font-weight: 600;
}

.running-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(67,160,71,0.1); color: #2e7d32;
  padding: 5px 12px; border-radius: 20px;
  font-family: 'Inter', 'Roboto', sans-serif; font-size: 12px; font-weight: 600;
}
.running-badge__dot {
  width: 7px; height: 7px; border-radius: 50%; background: #43a047;
  box-shadow: 0 0 0 2px rgba(67,160,71,0.25);
}

.start-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important; font-weight: 600 !important;
  text-transform: none !important; border-radius: 8px !important;
  height: 32px !important; padding: 0 14px !important; font-size: 13px !important;
  box-shadow: 0 2px 6px rgba(21,101,192,0.3) !important;
}

.modern-empty-state {
  padding: 60px 24px; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.modern-empty-state__icon { font-size: 48px !important; color: #cbd5e1 !important; }
.modern-empty-state__text { font-family: 'Inter', 'Roboto', sans-serif; font-size: 15px; color: #94a3b8; margin: 0; }
</style>
